// ===========================================
// Lesson Notes Button Component
// ===========================================
// Shows note completion status for a lesson and provides view/edit actions
// - If no notes: "Notes" button (outlined)
// - If partial notes: "Notes (X/Y)" button (warning color)
// - If complete: "Notes (Complete)" button (success color)

import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Box,
  Typography,
  List,
  ListItem,
  Chip,
  Skeleton,
  Divider,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Note as NoteIcon,
  ExpandMore as ExpandMoreIcon,
  Lock as LockIcon,
  Person as PersonIcon,
  School as SchoolIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import {
  useNotesByLesson,
  useLessonNoteCompletion,
} from '../../hooks/useNotes';
import {
  Note,
  NoteVisibility,
  getNoteStatusColor,
  getNoteVisibilityLabel,
  getNoteVisibilityColor,
  isClassNote,
} from '../../api/notes.api';
import NoteEditor from './NoteEditor';

// ===========================================
// TYPES
// ===========================================

interface LessonNotesButtonProps {
  lessonId: string;
  lessonName: string;
  date: Date;
  // When true (used for hybrid individual sessions), the Class Notes tab in
  // NoteEditor is suppressed and completion ignores `classNoteComplete`.
  hideClassNotesTab?: boolean;
  // When set, the view modal's student-notes list is filtered to just this
  // student. Used alongside hideClassNotesTab for hybrid individual sessions
  // so a teacher viewing Liam's Wed 17:00 row doesn't see Sophia's Wed 16:00
  // notes (same lessonId + date but different bookings).
  studentId?: string;
}

// ===========================================
// HELPER FUNCTIONS
// ===========================================

function getVisibilityIcon(visibility: NoteVisibility) {
  switch (visibility) {
    case 'TEACHERS_ONLY':
      return <LockIcon fontSize="small" />;
    case 'PARENTS':
      return <PersonIcon fontSize="small" />;
    case 'STUDENTS':
      return <SchoolIcon fontSize="small" />;
    default:
      return <NoteIcon fontSize="small" />;
  }
}

// ===========================================
// COMPONENT
// ===========================================

export default function LessonNotesButton({
  lessonId,
  lessonName,
  date,
  hideClassNotesTab,
  studentId,
}: LessonNotesButtonProps) {
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);

  const dateStr = format(date, 'yyyy-MM-dd');

  // Fetch notes and completion status
  const { data: notes, isLoading: isLoadingNotes, refetch } = useNotesByLesson(lessonId, { date: dateStr });
  const { data: completion, isLoading: isLoadingCompletion } = useLessonNoteCompletion(
    lessonId,
    dateStr,
    { skipClassNotes: hideClassNotesTab }
  );

  const isLoading = isLoadingNotes || isLoadingCompletion;

  // Separate class notes and student notes. When studentId is provided
  // (hybrid individual sessions) the student-notes list is scoped to that
  // single student so we don't surface another booking's notes from the same
  // lesson+date.
  const classNotes = notes?.filter((n) => isClassNote(n)) || [];
  const studentNotes =
    notes?.filter((n) => n.studentId && (!studentId || n.studentId === studentId)) || [];

  // Group student notes by student
  const studentNotesMap = studentNotes.reduce((acc, note) => {
    const noteStudentId = note.studentId!;
    if (!acc[noteStudentId]) {
      acc[noteStudentId] = {
        studentName: note.student ? `${note.student.firstName} ${note.student.lastName}` : 'Unknown Student',
        notes: [],
      };
    }
    acc[noteStudentId].notes.push(note);
    return acc;
  }, {} as Record<string, { studentName: string; notes: Note[] }>);

  const hasNotes = (notes?.length || 0) > 0;
  const isComplete = completion?.status === 'COMPLETE';
  const isPartial = completion?.status === 'PARTIAL';

  const handleViewClick = () => {
    setViewModalOpen(true);
  };

  const handleAddClick = () => {
    setEditModalOpen(true);
  };

  const handleCloseView = () => {
    setViewModalOpen(false);
  };

  const handleCloseEdit = () => {
    setEditModalOpen(false);
    refetch();
  };

  const handleEditFromView = () => {
    setViewModalOpen(false);
    setEditModalOpen(true);
  };

  if (isLoading) {
    return <Skeleton variant="rectangular" width={100} height={32} sx={{ borderRadius: 1 }} />;
  }

  // Determine button appearance based on completion status
  const getButtonSx = () => {
    if (isComplete) {
      return {
        variant: 'contained' as const,
        sx: {
          bgcolor: 'rgba(150,218,201,0.2)',
          color: '#3d9e84',
          boxShadow: 'none',
          '&:hover': { bgcolor: 'rgba(150,218,201,0.35)', boxShadow: 'none' },
        },
      };
    }
    if (isPartial) {
      return {
        variant: 'contained' as const,
        sx: {
          bgcolor: 'rgba(150,218,201,0.12)',
          color: '#3d9e84',
          boxShadow: 'none',
          '&:hover': { bgcolor: 'rgba(150,218,201,0.25)', boxShadow: 'none' },
        },
      };
    }
    return {
      variant: 'outlined' as const,
      sx: {
        borderColor: '#96DAC9',
        color: '#3d9e84',
        '&:hover': { borderColor: '#7cc8b5', bgcolor: 'rgba(150,218,201,0.08)' },
      },
    };
  };

  const buttonStyleProps = getButtonSx();

  return (
    <>
      {hasNotes ? (
        <Button
          size="small"
          variant={buttonStyleProps.variant}
          startIcon={<NoteIcon />}
          onClick={handleViewClick}
          sx={buttonStyleProps.sx}
        >
          Notes
        </Button>
      ) : (
        <Button
          size="small"
          variant="outlined"
          startIcon={<NoteIcon />}
          onClick={handleAddClick}
          sx={{
            borderColor: '#96DAC9',
            color: '#5ba893',
            '&:hover': { borderColor: '#7cc8b5', bgcolor: 'rgba(150,218,201,0.08)' },
          }}
        >
          Notes
        </Button>
      )}

      {/* View Notes Modal */}
      <Dialog
        open={viewModalOpen}
        onClose={handleCloseView}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h6">Notes</Typography>
              <Typography variant="body2" color="text.secondary">
                {lessonName} - {format(date, 'EEEE, d MMMM yyyy')}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                label={completion?.status || 'PENDING'}
                color={getNoteStatusColor(completion?.status || 'PENDING')}
                size="small"
              />
              <IconButton onClick={handleCloseView}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {/* Class Notes Section — suppressed for hybrid individual sessions */}
          {!hideClassNotesTab && (
            <>
              <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 1 }}>
                <SchoolIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                Class Notes
              </Typography>

              {classNotes.length === 0 ? (
                <Alert severity="info" sx={{ mb: 2 }}>No class notes added yet.</Alert>
              ) : (
                <List disablePadding sx={{ mb: 2 }}>
                  {classNotes.map((note) => (
                    <ListItem
                      key={note.id}
                      sx={{
                        py: 1.5,
                        px: 2,
                        mb: 1,
                        bgcolor: 'background.paper',
                        borderRadius: 1,
                        border: 1,
                        borderColor: 'divider',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', mb: 1 }}>
                        {getVisibilityIcon(note.visibility)}
                        <Chip
                          label={getNoteVisibilityLabel(note.visibility)}
                          color={getNoteVisibilityColor(note.visibility)}
                          size="small"
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                          {note.author ? `by ${note.author.firstName} ${note.author.lastName} · ` : ''}
                          {format(new Date(note.updatedAt), 'd/M/yy h:mma')}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {note.content}
                      </Typography>
                    </ListItem>
                  ))}
                </List>
              )}

              <Divider sx={{ my: 2 }} />
            </>
          )}

          {/* Student Notes Section */}
          <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 1 }}>
            <PersonIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
            Student Notes
            {completion && (
              <Chip
                label={`${completion.completedStudentNotes}/${completion.enrolledStudentCount}`}
                size="small"
                color={completion.completedStudentNotes === completion.enrolledStudentCount ? 'success' : 'warning'}
                sx={{ ml: 1 }}
              />
            )}
          </Typography>

          {Object.keys(studentNotesMap).length === 0 ? (
            <Alert severity="info">No student notes added yet.</Alert>
          ) : (
            Object.entries(studentNotesMap).map(([entryStudentId, { studentName, notes: sNotes }]) => (
              <Accordion key={entryStudentId} defaultExpanded={sNotes.length > 0}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography fontWeight="medium">{studentName}</Typography>
                  <Box sx={{ ml: 2, display: 'flex', gap: 0.5 }}>
                    {sNotes.map((n) => (
                      <Chip
                        key={n.id}
                        label={getNoteVisibilityLabel(n.visibility)}
                        color={getNoteVisibilityColor(n.visibility)}
                        size="small"
                      />
                    ))}
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <List disablePadding>
                    {sNotes.map((note) => (
                      <ListItem
                        key={note.id}
                        sx={{
                          py: 1.5,
                          px: 2,
                          mb: 1,
                          bgcolor: 'grey.50',
                          borderRadius: 1,
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', mb: 1 }}>
                          {getVisibilityIcon(note.visibility)}
                          <Chip
                            label={getNoteVisibilityLabel(note.visibility)}
                            color={getNoteVisibilityColor(note.visibility)}
                            size="small"
                          />
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                            {note.author ? `by ${note.author.firstName} ${note.author.lastName} · ` : ''}
                            {format(new Date(note.updatedAt), 'd/M/yy h:mma')}
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {note.content}
                        </Typography>
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            ))
          )}

          {/* Missing Student Notes */}
          {completion && completion.missingStudentNotes.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Missing notes for:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {completion.missingStudentNotes.map((s) => (
                  <Chip
                    key={s.studentId}
                    label={s.studentName}
                    size="small"
                    variant="outlined"
                    color="warning"
                  />
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseView}>Close</Button>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={handleEditFromView}
          >
            Edit Notes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit/Add Notes Modal */}
      <Dialog
        open={editModalOpen}
        onClose={handleCloseEdit}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">
              {hasNotes ? 'Edit Notes' : 'Add Notes'}
            </Typography>
            <IconButton onClick={handleCloseEdit}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <NoteEditor
            lessonId={lessonId}
            lessonName={lessonName}
            date={date}
            onClose={handleCloseEdit}
            hideClassNotesTab={hideClassNotesTab}
            studentId={studentId}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
